import { clsx, type ClassValue } from 'clsx';
import { AlertTriangle, Check, Loader2, X } from 'lucide-react';
import { Dialog, DropdownMenu, Tabs as TabsPrimitive } from 'radix-ui';
import {
  forwardRef,
  useLayoutEffect,
  useRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

/** The app's small component kit: Tailwind on top of Radix primitives, themed by the brand variables. */

export function cn(...values: ClassValue[]): string {
  return clsx(values);
}

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md';

export function buttonClass(variant: Variant = 'secondary', size: Size = 'md'): string {
  return cn(
    'inline-flex items-center justify-center gap-1.5 rounded-md font-medium whitespace-nowrap transition-colors disabled:cursor-not-allowed disabled:opacity-50',
    size === 'sm' ? 'h-8 px-2.5 text-xs' : 'h-9 px-3.5 text-sm',
    variant === 'primary' && 'bg-brand text-white shadow-sm hover:bg-brand-deep',
    variant === 'secondary' &&
      'border border-slate-300 bg-surface text-slate-800 shadow-sm hover:bg-slate-50',
    variant === 'ghost' && 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
    variant === 'danger' && 'bg-danger text-white shadow-sm hover:bg-danger-strong',
  );
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  busy?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant, size, busy, className, children, disabled, type, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={cn(buttonClass(variant, size), className)}
      disabled={Boolean(disabled) || Boolean(busy)}
      {...rest}
    >
      {busy && <Loader2 className="size-4 animate-spin" aria-hidden />}
      {children}
    </button>
  );
});

const control =
  'block w-full rounded-md border border-slate-300 bg-surface px-3 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand focus:ring-2 focus:ring-brand-muted focus:outline-none disabled:bg-slate-100 disabled:text-slate-500 aria-[invalid=true]:border-red-500';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return <input ref={ref} className={cn(control, 'h-9', className)} {...rest} />;
  },
);

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, ...rest }, ref) {
  return <textarea ref={ref} className={cn(control, 'min-h-24 py-2', className)} {...rest} />;
});

export const NativeSelect = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function NativeSelect({ className, ...rest }, ref) {
    return <select ref={ref} className={cn(control, 'h-9 pr-8', className)} {...rest} />;
  },
);

export function Label({
  htmlFor,
  children,
  required,
}: {
  htmlFor?: string;
  children: ReactNode;
  required?: boolean;
}) {
  return (
    <label htmlFor={htmlFor} className="mb-1 block text-xs font-medium text-slate-600">
      {children}
      {required && <span className="ml-0.5 text-red-600">*</span>}
    </label>
  );
}

/**
 * A titled panel. The `dashboard` look is the owner dashboard's, as on databink.com's sample
 * screen: rounder, a hairline border and a bolder title with no rule under it.
 */
export function Card({
  title,
  actions,
  children,
  className,
  padded = true,
  look = 'plain',
  ...rest
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  padded?: boolean;
  look?: 'plain' | 'dashboard';
  /** Marks the panel for the read-only view's tour. */
  'data-tour'?: string;
}) {
  const dashboard = look === 'dashboard';
  return (
    <section
      className={cn(
        dashboard
          ? 'rounded-xl border border-line bg-surface'
          : 'rounded-lg border border-slate-200 bg-surface shadow-sm',
        className,
      )}
      {...rest}
    >
      {(title || actions) && (
        <header
          className={cn(
            'flex items-center justify-between gap-3 px-4',
            dashboard ? 'pt-3.5 pb-1' : 'border-b border-slate-100 py-3',
          )}
        >
          {title && (
            <h2
              className={cn(
                'text-sm',
                dashboard ? 'font-bold text-ink' : 'font-semibold text-slate-800',
              )}
            >
              {title}
            </h2>
          )}
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={padded ? 'p-4' : ''}>{children}</div>
    </section>
  );
}

export type Tone = 'neutral' | 'brand' | 'green' | 'amber' | 'red';

export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap ring-1 ring-inset',
        tone === 'neutral' && 'bg-slate-100 text-slate-700 ring-slate-200',
        tone === 'brand' && 'bg-brand-soft text-brand-strong ring-brand-muted',
        tone === 'green' && 'bg-emerald-50 text-emerald-700 ring-emerald-200',
        tone === 'amber' && 'bg-amber-50 text-amber-800 ring-amber-200',
        tone === 'red' && 'bg-red-50 text-red-700 ring-red-200',
      )}
    >
      {children}
    </span>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 p-6 text-sm text-slate-500" role="status">
      <Loader2 className="size-4 animate-spin" aria-hidden />
      {label}
    </div>
  );
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="px-6 py-10 text-center">
      <p className="text-sm font-medium text-slate-700">{title}</p>
      {children && <div className="mt-1 text-sm text-slate-500">{children}</div>}
    </div>
  );
}

export function ErrorBanner({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div
      role="alert"
      className="flex gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
    >
      <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
      <div>
        {title && <p className="font-medium">{title}</p>}
        <div>{children}</div>
      </div>
    </div>
  );
}

/**
 * A table. Wider than its place, it scrolls sideways inside itself; or, with `stack`, each row
 * is laid out as a card instead (the `table[data-stacked]` rules in styles.css), each value
 * under its column's name, so a phone or a narrow panel never scrolls sideways. A stacked
 * table's cells take their column's `label`, and the row's leading cell is `primary`, which
 * heads the card. `onStackedChange` hears when it turns into cards and back, for a screen
 * that offers what the hidden column headings did, such as sorting.
 */
export function Table({
  children,
  stack = false,
  className,
  onStackedChange,
}: {
  children: ReactNode;
  stack?: boolean;
  className?: string;
  onStackedChange?: (stacked: boolean) => void;
}) {
  const place = useRef<HTMLDivElement>(null);
  const table = useRef<HTMLTableElement>(null);
  const heard = useRef(onStackedChange);
  useLayoutEffect(() => {
    heard.current = onStackedChange;
  });
  useLayoutEffect(() => {
    const outer = place.current;
    const inner = table.current;
    if (!stack || !outer || !inner) return;
    // Null until first measured, so the first measure is always heard.
    let stacked: boolean | null = null;
    // Laid out as a table, does it fit? The attribute is the app's, not React's, so measuring
    // it as a table and putting the cards back happens in one go, before anything is drawn.
    const fit = () => {
      inner.removeAttribute('data-stacked');
      const now = inner.offsetWidth > outer.clientWidth + 1;
      if (now) inner.setAttribute('data-stacked', '');
      if (now !== stacked) {
        stacked = now;
        heard.current?.(now);
      }
    };
    fit();
    // Rows, columns or values changing: measured again before the change is drawn.
    const changes = new MutationObserver(fit);
    changes.observe(inner, { childList: true, subtree: true, characterData: true });
    // Its place changing size (the window, a panel opening): measured on the next frame, as
    // changing the layout from inside a resize notice makes the browser report a loop.
    let frame = 0;
    const resized = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(fit);
    });
    resized.observe(outer);
    return () => {
      changes.disconnect();
      resized.disconnect();
      cancelAnimationFrame(frame);
      inner.removeAttribute('data-stacked');
    };
  }, [stack]);
  return (
    <div ref={place} className="overflow-x-auto">
      <table ref={table} className={cn('w-full border-collapse text-sm', className)}>
        {children}
      </table>
    </div>
  );
}

export function TH({
  children,
  align = 'left',
  className,
}: {
  children?: ReactNode;
  align?: 'left' | 'right';
  className?: string;
}) {
  return (
    <th
      className={cn(
        'border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold tracking-wide whitespace-nowrap text-slate-500 uppercase',
        align === 'right' ? 'text-right' : 'text-left',
        className,
      )}
    >
      {children}
    </th>
  );
}

export function TD({
  children,
  align = 'left',
  className,
  label,
  primary = false,
}: {
  children?: ReactNode;
  align?: 'left' | 'right';
  className?: string;
  /** The column's name, shown above the value when a stacked table lays its rows out as cards. */
  label?: string;
  /** The row's leading cell (its title), which heads the card when the rows are cards. */
  primary?: boolean;
}) {
  return (
    <td
      data-label={label}
      data-primary={primary ? '' : undefined}
      className={cn(
        'border-b border-slate-100 px-3 py-2 align-top',
        align === 'right' && 'text-right tabular-nums',
        className,
      )}
    >
      {children}
    </td>
  );
}

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  wide,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-night/40" />
        <Dialog.Content
          className={cn(
            'fixed top-1/2 left-1/2 z-50 max-h-[90vh] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg bg-surface shadow-xl',
            wide ? 'max-w-4xl' : 'max-w-lg',
          )}
        >
          <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
            <div>
              <Dialog.Title className="text-base font-semibold text-slate-900">
                {title}
              </Dialog.Title>
              {description ? (
                <Dialog.Description className="mt-1 text-sm text-slate-500">
                  {description}
                </Dialog.Description>
              ) : (
                <Dialog.Description className="sr-only">{title}</Dialog.Description>
              )}
            </div>
            <Dialog.Close className={buttonClass('ghost', 'sm')} aria-label="Close">
              <X className="size-4" />
            </Dialog.Close>
          </div>
          {children && <div className="px-5 py-4">{children}</div>}
          {footer && (
            <div className="flex justify-end gap-2 border-t border-slate-100 bg-slate-50 px-5 py-3">
              {footer}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export const Tabs = TabsPrimitive.Root;

export function TabsList({ children }: { children: ReactNode }) {
  return (
    <TabsPrimitive.List className="flex gap-1 overflow-x-auto border-b border-slate-200">
      {children}
    </TabsPrimitive.List>
  );
}

export function TabsTrigger({ value, children }: { value: string; children: ReactNode }) {
  return (
    <TabsPrimitive.Trigger
      value={value}
      className="-mb-px border-b-2 border-transparent px-3 py-2 text-sm font-medium whitespace-nowrap text-slate-500 hover:text-slate-800 data-[state=active]:border-brand data-[state=active]:text-brand-strong"
    >
      {children}
    </TabsPrimitive.Trigger>
  );
}

export function TabsContent({ value, children }: { value: string; children: ReactNode }) {
  return (
    <TabsPrimitive.Content value={value} className="pt-4 focus-visible:outline-none">
      {children}
    </TabsPrimitive.Content>
  );
}

export const Menu = DropdownMenu.Root;
export const MenuTrigger = DropdownMenu.Trigger;

export function MenuContent({ children }: { children: ReactNode }) {
  return (
    <DropdownMenu.Portal>
      <DropdownMenu.Content
        align="end"
        sideOffset={6}
        className="z-50 min-w-48 rounded-md border border-slate-200 bg-surface p-1 shadow-lg"
      >
        {children}
      </DropdownMenu.Content>
    </DropdownMenu.Portal>
  );
}

export function MenuItem({
  children,
  onSelect,
  disabled,
}: {
  children: ReactNode;
  onSelect?: () => void;
  disabled?: boolean;
}) {
  return (
    <DropdownMenu.Item
      {...(onSelect && { onSelect })}
      {...(disabled !== undefined && { disabled })}
      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-slate-700 outline-none data-[disabled]:cursor-default data-[disabled]:opacity-50 data-[highlighted]:bg-slate-100"
    >
      {children}
    </DropdownMenu.Item>
  );
}

/** A set of menu items of which one is chosen, marked with a tick; picking one keeps the menu open. */
export function MenuChoices<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: T;
  onChange: (value: T) => void;
  options: readonly { value: T; label: string; icon?: ReactNode }[];
}) {
  return (
    <DropdownMenu.RadioGroup
      aria-label={label}
      value={value}
      onValueChange={(next) => onChange(next as T)}
    >
      {options.map((option) => (
        <DropdownMenu.RadioItem
          key={option.value}
          value={option.value}
          onSelect={(event) => event.preventDefault()}
          className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-slate-700 outline-none data-[highlighted]:bg-slate-100"
        >
          {option.icon}
          <span className="flex-1">{option.label}</span>
          <DropdownMenu.ItemIndicator>
            <Check className="size-4 text-brand-strong" aria-hidden />
          </DropdownMenu.ItemIndicator>
        </DropdownMenu.RadioItem>
      ))}
    </DropdownMenu.RadioGroup>
  );
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return (
    <DropdownMenu.Label className="px-2 py-1.5 text-xs text-slate-500">
      {children}
    </DropdownMenu.Label>
  );
}

export function MenuSeparator() {
  return <DropdownMenu.Separator className="my-1 h-px bg-slate-100" />;
}

export function PageHeader({
  title,
  subtitle,
  actions,
  eyebrow,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  eyebrow?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0">
        {eyebrow && <div className="mb-1 text-xs font-medium text-slate-500">{eyebrow}</div>}
        <h1 className="text-xl font-semibold break-words text-slate-900 sm:truncate">{title}</h1>
        {subtitle && <div className="mt-1 text-sm text-slate-500">{subtitle}</div>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}
